import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FolderOpen,
  Package,
  FileText,
  Users,
  TrendingUp,
  Clock,
  RefreshCw,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { pb } from '../lib/pocketbaseClient';

// ------------------------------
// ??????? ?????????
// ------------------------------

/** ?? ? ????? ??????? ?????? (YYYY-MM-DD) */
const getLast7Days = () => {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().split('T')[0]);
  }
  return days;
};

/** ???? ????????? ??? ?????? ?????? */
const fetchTotal = async (collection) => {
  try {
    const result = await pb.collection(collection).getList(1, 1, {
      skipTotal: false,
    });
    return result.totalItems;
  } catch (err) {
    console.error(`Error fetching ${collection} count:`, err);
    return 0;
  }
};

/** ?? ? ???? ???? ???????? ???? ???????? ?????? (??????????) */
const fetchWeeklyUploads = async (collection, dateField = 'created') => {
  try {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const filter = `${dateField} >= "${sevenDaysAgo.toISOString()}"`;
    const result = await pb.collection(collection).getList(1, 500, {
      filter,
      sort: dateField,
    });
    const days = getLast7Days();
    const counts = days.map((day) => ({ day, uploads: 0 }));
    result.items.forEach((item) => {
      const day = item[dateField]?.split('T')[0];
      const found = counts.find((c) => c.day === day);
      if (found) found.uploads += 1;
    });
    return counts;
  } catch (err) {
    console.error(`Error fetching weekly uploads for ${collection}:`, err);
    return getLast7Days().map((day) => ({ day, uploads: 0 }));
  }
};

/** ?????? ??????? ???? ??????? ????????? ?????? */
const fetchRecentActivities = async (collections, limit = 3) => {
  try {
    const all = [];
    for (const col of collections) {
      const result = await pb.collection(col).getList(1, limit, {
        sort: '-created',
      });
      result.items.forEach((item) => {
        let action = '';
        let label = col;
        if (col === 'gallery_images') label = 'File';
        else if (col === 'products') label = 'Product';
        else if (col === 'blogs') label = 'Blog';
        else if (col === 'leads') label = 'Customer';
        const name = item.title || item.name || item.id;
        action = `New ${label} added: "${name}"`;
        all.push({
          id: item.id,
          action,
          time: new Date(item.created).toLocaleString(),
          timestamp: new Date(item.created).getTime(),
        });
      });
    }
    all.sort((a, b) => b.timestamp - a.timestamp);
    return all.slice(0, 5);
  } catch (err) {
    console.error('Error fetching activities:', err);
    return [];
  }
};

/** ?? ? ????? ???? ???? ? ????? ?????? ??????? % ??? ??? */
const fetchTrend = async (collection, dateField = 'created') => {
  try {
    const now = new Date();
    const last7Start = new Date(now);
    last7Start.setDate(last7Start.getDate() - 7);
    const prev7Start = new Date(last7Start);
    prev7Start.setDate(prev7Start.getDate() - 7);

    const lastFilter = `${dateField} >= "${last7Start.toISOString()}"`;
    const prevFilter = `${dateField} >= "${prev7Start.toISOString()}" && ${dateField} < "${last7Start.toISOString()}"`;

    const [lastResult, prevResult] = await Promise.all([
      pb.collection(collection).getList(1, 1, { filter: lastFilter, skipTotal: false }),
      pb.collection(collection).getList(1, 1, { filter: prevFilter, skipTotal: false }),
    ]);

    const lastCount = lastResult.totalItems;
    const prevCount = prevResult.totalItems;
    if (prevCount === 0) return { trend: 0, direction: 'neutral' };
    const change = ((lastCount - prevCount) / prevCount) * 100;
    return {
      trend: Math.round(change * 10) / 10,
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral',
    };
  } catch (err) {
    console.error('Error fetching trend:', err);
    return { trend: 0, direction: 'neutral' };
  }
};

/** ????????? ????????????? (??? ??????? ????) */
const fetchCategoryDistribution = async (collection, categoryField = 'category') => {
  try {
    const result = await pb.collection(collection).getList(1, 200);
    const map = {};
    result.items.forEach((item) => {
      const cat = item[categoryField] || 'Uncategorized';
      map[cat] = (map[cat] || 0) + 1;
    });
    return Object.keys(map).map((key) => ({
      name: key,
      value: map[key],
    }));
  } catch (err) {
    console.error('Error fetching category distribution:', err);
    return [];
  }
};

// ------------------------------
// ??? ??????????
// ------------------------------

const Dashboard = () => {
  // ?????
  const [stats, setStats] = useState([
    { label: 'Total Files', value: '0', icon: FolderOpen, color: 'from-blue-500 to-blue-600', trend: 0, direction: 'neutral' },
    { label: 'Products', value: '0', icon: Package, color: 'from-green-500 to-green-600', trend: 0, direction: 'neutral' },
    { label: 'Blog Posts', value: '0', icon: FileText, color: 'from-purple-500 to-purple-600', trend: 0, direction: 'neutral' },
    { label: 'Customers', value: '0', icon: Users, color: 'from-amber-500 to-amber-600', trend: 0, direction: 'neutral' },
  ]);
  const [chartData, setChartData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // ???? ??? ?????
  const loadData = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      // ?. ?????? ? ??????? ??????
      const collections = ['gallery_images', 'products', 'blogs', 'leads'];
      const [files, products, blogs, leads] = await Promise.all(
        collections.map((col) => fetchTotal(col))
      );
      const [filesTrend, productsTrend, blogsTrend, leadsTrend] = await Promise.all(
        collections.map((col) => fetchTrend(col))
      );

      setStats([
        {
          label: 'Total Files',
          value: files.toLocaleString(),
          icon: FolderOpen,
          color: 'from-blue-500 to-blue-600',
          trend: filesTrend.trend,
          direction: filesTrend.direction,
        },
        {
          label: 'Products',
          value: products.toLocaleString(),
          icon: Package,
          color: 'from-green-500 to-green-600',
          trend: productsTrend.trend,
          direction: productsTrend.direction,
        },
        {
          label: 'Blog Posts',
          value: blogs.toLocaleString(),
          icon: FileText,
          color: 'from-purple-500 to-purple-600',
          trend: blogsTrend.trend,
          direction: blogsTrend.direction,
        },
        {
          label: 'Customers',
          value: leads.toLocaleString(),
          icon: Users,
          color: 'from-amber-500 to-amber-600',
          trend: leadsTrend.trend,
          direction: leadsTrend.direction,
        },
      ]);

      // ?. ????????? ????? (gallery_images ????)
      const weekly = await fetchWeeklyUploads('gallery_images');
      setChartData(weekly);

      // ?. ????????? ????????????? (gallery_images ????)
      const catData = await fetchCategoryDistribution('gallery_images');
      setCategoryData(catData);

      // ?. ?????????? ?????????
      const acts = await fetchRecentActivities(['gallery_images', 'products', 'blogs', 'leads']);
      setActivities(acts);
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError('Failed to load dashboard data. Please refresh.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ??? ??????? ??
  const COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#ec4899'];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-500 text-lg font-medium">Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadData}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* ????? */}
      <div className="flex flex-wrap justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Real-time overview of your files, products, blogs, and customers.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">
            Last updated: {new Date().toLocaleString()}
          </span>
          <button
            onClick={loadData}
            disabled={refreshing}
            className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ??????? ????????? */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition"
          >
            <div className="flex items-center justify-between mb-4">
              <div
                className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-white`}
              >
                <stat.icon className="w-6 h-6" />
              </div>
              <div className="flex items-center gap-1 text-sm font-medium">
                {stat.direction === 'up' && (
                  <span className="text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full flex items-center">
                    <ArrowUp className="w-3 h-3 mr-1" />
                    {stat.trend}%
                  </span>
                )}
                {stat.direction === 'down' && (
                  <span className="text-red-600 bg-red-50 px-2 py-1 rounded-full flex items-center">
                    <ArrowDown className="w-3 h-3 mr-1" />
                    {Math.abs(stat.trend)}%
                  </span>
                )}
                {stat.direction === 'neutral' && (
                  <span className="text-gray-500 bg-gray-50 px-2 py-1 rounded-full flex items-center">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    0%
                  </span>
                )}
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800">{stat.value}</h3>
            <p className="text-sm text-gray-500">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* ????? ? ???????????? */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ????????? ????? ??? ????? */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-500" />
            Weekly Uploads (Files)
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="day" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
                cursor={{ fill: '#f1f5f9' }}
              />
              <Bar dataKey="uploads" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ???????????? ? ????????? ??? ????? (??? ?????) */}
        <div className="space-y-6">
          {/* ???????????? */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Recent Activity
            </h3>
            <div className="space-y-4 max-h-48 overflow-y-auto">
              {activities.length === 0 ? (
                <p className="text-sm text-gray-400">No recent activity</p>
              ) : (
                activities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm text-gray-700">{activity.action}</p>
                      <p className="text-xs text-gray-400">{activity.time}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ????????? ????????????? (??? ?????) */}
          {categoryData.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <span className="w-5 h-5 text-amber-500">??</span>
                Files by Category
              </h3>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-2 mt-2">
                {categoryData.map((cat, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 rounded-full"
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                    ></span>
                    {cat.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;